import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Slider, message } from 'antd';
import { hslToRgb, rgbToHex, rgbToHue, hexToRgb, isHexColor } from 'utils/colors';
import './HueSlider.scss';

/**
 * HueSlider component for selecting a hue value
 *
 * @component
 * @param {Object} props
 * @param {Object} props.value - The current color value object.
 * @param {Function} props.onChange - Callback fired when hue value changes. Receives new value.
 *
 * @example
 * <HueSlider
 *   value={{ rgb: { r: 255, g: 0, b: 0, a: 1 }, hex: '#ff0000' }}
 *   onChange={handleChange}
 * />
 */
const HueSlider = ({ value, onChange }) => {
  const [hue, setHue] = useState(0);
  const [color, setColor] = useState({ ...value });
  const [hex, setHex] = useState('#000000');

  useEffect(() => {
    if (value?.hex && color.hex !== value.hex) {
      let { rgb } = value;
      if (!rgb) {
        rgb = hexToRgb(value.hex);
      }
      setHue(rgbToHue(rgb.r, rgb.g, rgb.b));
      setHex(value.hex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (hue) => {
    hue = Number(hue);
    setHue(hue);
    debounceChange(hue);
  };

  const debounceChange = useCallback(
    (hue) => {
      const rgb = hslToRgb(hue, 100, 50);
      const hex = rgbToHex(rgb.r, rgb.g, rgb.b);

      rgb.a = value?.rgb?.a ?? 1;
      const color = { hex, rgb };

      setColor(color);
      onChange(color);
      setHex(hex);
    },
    [onChange, value]
  );

  const processHex = (hex, value) => {
    if (!isHexColor(hex)) {
      return null;
    }

    const rgb = hexToRgb(hex);
    const hue = rgbToHue(rgb.r, rgb.g, rgb.b);
    rgb.a = value?.rgb?.a ?? 1;

    onChange({ hex, rgb });
    return hue;
  };

  const handleHexChange = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const raw = e.target.value.toUpperCase();
    const hex = `#${raw.replace(/[^0-9A-F]/g, '').substring(0, 6)}`;
    setHex(hex);
    const hue = processHex(hex, value);
    if (hue) {
      setHue(hue);
    }
  };

  const handlePaste = (e) => {
    try {
      const pastedText = (e.clipboardData || window.clipboardData).getData('text');
      if (!isHexColor(pastedText.trim())) {
        message.error('Invalid hex color');
      }
    } catch (error) {}
  };

  const handleBlur = (e) => {
    if (!isHexColor(e.target.value)) {
      message.error('Invalid hex color');
    }
  };

  return (
    <div className="custom-color-slider">
      <div className="label-with-input">
        <small className="image-crop-label">Color</small>
        <div className="hex-value-display">
          <input
            className="hex-value-input"
            type="text"
            value={hex}
            onChange={handleHexChange}
            onPaste={handlePaste}
            onBlur={handleBlur}
            maxLength={8}
          />
        </div>
      </div>

      <div className="hue-picker">
        <Slider
          step={1}
          min={0}
          max={360}
          value={hue}
          onChange={handleChange}
          tooltipVisible={false}
        />
      </div>
    </div>
  );
};

HueSlider.propTypes = {
  value: PropTypes.shape({
    rgb: PropTypes.shape({
      r: PropTypes.number,
      g: PropTypes.number,
      b: PropTypes.number,
      a: PropTypes.number,
    }),
    hex: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default HueSlider;
